import { useCallback, useEffect, useState } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import type { CreateSnippetBody, SnippetData } from '../services/blockServiceClient.ts';
import { createSnippet } from '../services/blockServiceClient.ts';
import { SnippetBodyFormat, SnippetProduct } from '../snippetTypes.ts';

export const useCreateSnippet = (
	onCreate: (snippetName: string, snippetDescription?: string) => void,
) => {
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);

	const create = useCallback(
		async (name: string, body: ADFEntity): Promise<SnippetData | null> => {
			const params: CreateSnippetBody = {
				name,
				body,
				bodyFormat: SnippetBodyFormat.ADF,
				product: SnippetProduct.CONFLUENCE,
			};

			try {
				setLoading(true);
				const newSnippet = await createSnippet(params);
				onCreate(newSnippet.name, newSnippet.description);
				return newSnippet;
			} catch (thrownError) {
				setError(thrownError);
			} finally {
				setLoading(false);
			}

			return null;
		},
		[onCreate, setError, setLoading],
	);

	useEffect(() => {
		if (!loading && error) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.INTEGRATIONS,
			});
		}
	}, [error, loading]);

	return { create, error, loading };
};
